/* ============================================================================
   Avatar — the profile picture when there is one, initials when there isn't.

   Every avatar in the app used to fall back to default_pic.png (a grey bust)
   or to a fa-user-circle glyph, so a table of twenty users was twenty
   identical silhouettes. Initials at least distinguish rows.

   The "dp" is treated as missing when it is null, undefined or blank, which
   is what the API sends for a user who never uploaded one. A URL that 404s
   still needs a runtime catch, so onError swaps the <img> out for the same
   initials the no-src path renders.

   Sizing and shape stay in CSS: pass the caller's own class (sb-user-avatar,
   uim-avatar, …) and it lands on the same element in both states, so the two
   states cannot drift apart.
   ========================================================================== */
import { useEffect, useState } from 'react'

export const initialsOf = (first, last, fallback) => {
  const a = String(first ?? '').trim()
  const b = String(last ?? '').trim()
  const letters = `${a.charAt(0)}${b.charAt(0)}`.trim()
  if (letters) return letters.toUpperCase()

  const f = String(fallback ?? '').trim()
  return f ? f.charAt(0).toUpperCase() : '?'
}

const Avatar = ({
  src,
  first,
  last,
  name,
  fallback,
  className = '',
  title,
  ...rest
}) => {
  /* some endpoints send one "name" field instead of first_name/last_name */
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean)
  const firstName = first ?? parts[0]
  const lastName = last ?? (parts.length > 1 ? parts[parts.length - 1] : '')

  const [failed, setFailed] = useState(false)

  /* a row re-used for a different user must retry that user's picture */
  useEffect(() => {
    setFailed(false)
  }, [src])

  const dp = typeof src === 'string' ? src.trim() : src
  const initials = initialsOf(firstName, lastName, fallback)
  const label = `${String(firstName ?? '').trim()} ${String(
    lastName ?? ''
  ).trim()}`.trim()

  if (dp && !failed) {
    return (
      <img
        src={dp}
        alt={label || 'Profile picture'}
        title={title}
        className={`avatar avatar-img ${className}`.trim()}
        onError={() => setFailed(true)}
        {...rest}
      />
    )
  }

  return (
    <span
      className={`avatar avatar-initials ${className}`.trim()}
      title={title}
      role="img"
      aria-label={label || 'Profile'}
      {...rest}
    >
      {initials}
    </span>
  )
}

export default Avatar
